import { createContext, ReactNode, useContext, useMemo } from 'react'

export interface FilesEntry {
  publicName: string | null
  fileName: string
  signedUrl: string
}

export type FilesMap = Record<string, FilesEntry>

// Lets the host app inject behavior the renderer needs but shouldn't own:
// today, scrolling to a section anchor (user-interface wires this to its
// eventBus). Admin can pass a different handler — or none, in which case
// internal anchors fall back to the default browser jump.
export interface ReportInteractions {
  onScrollToSection?: (sectionId: string) => void
  files?: FilesMap
  onFileClick?: (docId: string) => void
  onTokenCopied?: (label: string) => void
}

const ReportInteractionContext = createContext<ReportInteractions>({})

interface ProviderProps extends ReportInteractions {
  children: ReactNode
}

export const ReportInteractionProvider = ({
  children,
  onScrollToSection,
  files,
  onFileClick,
  onTokenCopied,
}: ProviderProps) => {
  const value = useMemo(
    () => ({ onScrollToSection, files, onFileClick, onTokenCopied }),
    [onScrollToSection, files, onFileClick, onTokenCopied]
  )
  return (
    <ReportInteractionContext.Provider value={value}>
      {children}
    </ReportInteractionContext.Provider>
  )
}

export const useReportInteractions = () => useContext(ReportInteractionContext)
